import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { LogIn, UserPlus, Ghost, X, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Wraps a game-mode page. If the user is not signed in, renders an overlay
 * prompting them to either Sign in / Create Account OR Play as Guest.
 * When authed, renders the children unchanged.
 *
 * Per-mode names (Casual / Multiplayer / Private / Practice) are passed in to
 * keep the prompt context-aware.
 */
export default function ModeGate({ mode = "this mode", accent = "blue", children, allowGuest = true }) {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [busy, setBusy] = useState(false);

  if (user) return children;

  const playAsGuest = async () => {
    setBusy(true);
    try {
      const r = await apiClient.post("/auth/guest");
      localStorage.setItem("ba_token", r.data.token);
      await refresh();
      toast.success(`Playing as ${r.data.user.username}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Guest sign-in failed");
    } finally {
      setBusy(false);
    }
  };

  const ringColor = {
    blue:   "rgba(59,130,246,0.55)",
    purple: "rgba(168,85,247,0.55)",
    teal:   "rgba(20,184,166,0.55)",
    amber:  "rgba(245,158,11,0.55)",
  }[accent] || "rgba(59,130,246,0.55)";

  return (
    <div className="relative min-h-[80vh] flex items-center justify-center px-4 py-10">
      {/* Faded preview of the mode page behind the modal */}
      <div className="absolute inset-0 pointer-events-none opacity-20 blur-sm">
        {children}
      </div>

      <AnimatePresence>
        <motion.div
          key="gate"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 240, damping: 22 }}
          className="relative w-full max-w-md sm:max-w-lg"
          data-testid="mode-gate"
        >
          <div className="sm-card p-8 sm:p-10"
            style={{ borderColor: ringColor, boxShadow: `0 0 36px ${ringColor}` }}>
            <button
              onClick={() => nav("/")}
              className="absolute top-4 right-4 text-faint hover:text-white"
              aria-label="Close"
              data-testid="gate-close"
            >
              <X size={18} />
            </button>

            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 grid place-items-center rounded-2xl border"
                style={{ borderColor: ringColor, background: `linear-gradient(160deg, rgba(11,14,30,0.7), transparent)` }}>
                <BookOpen size={28} className="text-blue" />
              </div>
              <div className="mt-4 font-mono tracking-[0.35em] uppercase text-xs text-soft">
                {mode.toUpperCase()} MODE
              </div>
              <h2 className="mt-2 font-heading font-black text-3xl text-white">
                SIGN IN TO CONTINUE
              </h2>
              <p className="mt-3 text-sm text-soft max-w-sm">
                Create an account to keep your ELO, climb the global ladder and unlock ranked rewards — or jump straight in as a Guest.
              </p>

              <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
                <button
                  onClick={() => nav("/login", { state: { from: loc.pathname } })}
                  className="sm-btn-primary px-5 py-3 flex items-center justify-center gap-2"
                  data-testid="gate-signin"
                >
                  <LogIn size={16} /> SIGN IN
                </button>
                <button
                  onClick={() => nav("/signup", { state: { from: loc.pathname } })}
                  className="sm-btn-outline px-5 py-3 flex items-center justify-center gap-2"
                  data-testid="gate-signup"
                >
                  <UserPlus size={16} /> CREATE ACCOUNT
                </button>
              </div>

              {allowGuest && (
                <button
                  onClick={playAsGuest}
                  disabled={busy}
                  className="mt-4 w-full px-5 py-3 rounded-xl border border-white/15 bg-white/[0.03] hover:bg-white/[0.06] transition-colors font-mono uppercase tracking-[0.25em] text-xs text-white/85 flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="gate-guest"
                >
                  <Ghost size={16} className="text-purple" />
                  {busy ? "Spinning up guest..." : "Play as Guest"}
                </button>
              )}

              <div className="mt-5 text-[10px] font-mono tracking-[0.25em] text-faint uppercase">
                Guest stats are not added to the ladder
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
